import { useMemo, useState } from "react";
import { Paper, Skeleton, Autocomplete, TextField, Chip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";

import { RootState } from "../../../state/store";
import { useAppDispatch } from "../../../state/hooks";
import { setTargetCategory } from "../../../state/reducers/dashboardReducer";
import { TARGET_CATEGORY_NAMES, TargetCategoryName } from "../../../types/TargetCategory";
import useShiftTimes from "../../Hooks/useShiftTimes";
import { useDashboardCard } from "../../Contexts/DashboardCardContext";
import { DashboardCardEmptyState } from "../DashboardCardEmptyState";
import { buildTargetGauges, elapsedFraction, loadingGaugeCount, TARGET_METRIC_COUNT, toggleGaugeKey } from "./targetGauges";
import { useCostUnit } from "../../Hooks/useCurrency";
import { GaugeMeter, GaugeMeterSkeleton } from "./GaugeMeter";
import { materialMovementQuery, plantStatusQuery, targetActualsQuery, targetsQuery } from "./dashboardQueries";

const GAUGE_GRID_CLASS = "overflow-y-auto flex-1 grid gap-3 px-3 py-3 animate-fade-in content-start grid-cols-2 @xl/card:grid-cols-3 @4xl/card:grid-cols-4 card-short:@xl/card:grid-cols-4";

export const TargetsCard: React.FC = () => {
  const { selectedDate, selectedShift } = useShiftTimes();
  const { isFullScreen, onScreen } = useDashboardCard();
  const dispatch = useAppDispatch();
  const category = useSelector((state: RootState) => state.dashboard.targetCategory);
  const [shownKeys, setShownKeys] = useState<string[] | null>(null);
  const costUnit = useCostUnit();

  const queryEnabled = selectedDate.isValid && !!selectedShift;

  const { data: targetsData, isLoading: targetsLoading } = useQuery({
    ...targetsQuery(selectedDate, category),
    enabled: queryEnabled,
  });

  const { data: materialMovement = null, isLoading: movementLoading } = useQuery({
    ...materialMovementQuery(selectedDate, selectedShift),
    enabled: queryEnabled,
    subscribed: onScreen,
  });

  const { data: formulaActuals = null } = useQuery({
    ...targetActualsQuery(selectedDate),
    enabled: queryEnabled,
  });

  const { isLoading: plantLoading, dataUpdatedAt } = useQuery({
    ...plantStatusQuery(selectedDate, selectedShift, false),
    enabled: queryEnabled,
    subscribed: onScreen,
  });

  const gauges = useMemo(() => {
    if (!targetsData) return [];

    const now = dataUpdatedAt > 0 ? DateTime.fromMillis(dataUpdatedAt) : DateTime.now();
    const elapsed = elapsedFraction(targetsData.dayStartUtc, targetsData.dayEndUtc, now);

    return buildTargetGauges(targetsData.entries, { materialMovement, formulaActuals }, elapsed)
      .map(gauge => ({ ...gauge, unit: costUnit(gauge.unit) }));
  }, [targetsData, materialMovement, formulaActuals, dataUpdatedAt, costUnit]);

  const gaugeKeys = useMemo(() => gauges.map(gauge => gauge.key), [gauges]);

  const shown = useMemo(
    () => (shownKeys === null ? gauges : gauges.filter(gauge => shownKeys.includes(gauge.key))),
    [gauges, shownKeys]
  );

  const showSkeleton = targetsLoading || movementLoading || plantLoading || !queryEnabled;
  const isEmpty = !showSkeleton && gauges.length === 0;

  return (
    <Paper className="!shadow-none !border-none !w-full h-full relative !bg-theme-surface-2 flex flex-col">
      <div className="flex overflow-x-auto overflow-y-hidden items-center gap-2 px-3 pt-3 pb-0.5">
        <Autocomplete
          size="small"
          className="min-w-[130px] flex-1"
          options={TARGET_CATEGORY_NAMES as readonly TargetCategoryName[]}
          value={category}
          disableClearable
          onChange={(_, value) => value && dispatch(setTargetCategory(value))}
          renderInput={(params) => <TextField {...params} label="Compare against" />}
        />
        <div className="flex items-center gap-0.5 flex-[4]">
          {showSkeleton && [...Array(TARGET_METRIC_COUNT)].map((_, i) => (
            <Skeleton key={i} animation="wave" variant="rounded" width={96} height={32} className="!rounded-full !bg-theme-table-heading shrink-0" />
          ))}
          {!showSkeleton && gauges.map(gauge => {
            const isShown = shownKeys === null || shownKeys.includes(gauge.key);

            return (
              <Chip
                key={gauge.key}
                label={gauge.label}
                size="small"
                aria-pressed={isShown}
                onClick={() => setShownKeys(toggleGaugeKey(shownKeys, gaugeKeys, gauge.key))}
                className={`!h-8 ${isShown
                  ? "!bg-theme-primary !text-theme-primary-contrast !border !border-theme-border"
                  : "!bg-theme-surface !text-theme-text-muted !border !border-theme-table-border"}`}
              />
            );
          })}
        </div>
      </div>

      {showSkeleton && (
        <div className={GAUGE_GRID_CLASS}>
          {[...Array(loadingGaugeCount(shownKeys))].map((_, i) => (
            <GaugeMeterSkeleton key={i} isCardFullScreen={isFullScreen} />
          ))}
        </div>
      )}

      {isEmpty && (
        <DashboardCardEmptyState
          isFullScreen={isFullScreen}
          title="No targets to compare"
          subtitle="No targets are set for this date, or the figures they compare against have not loaded."
        />
      )}

      {!showSkeleton && !isEmpty && shown.length > 0 && (
        <div className={GAUGE_GRID_CLASS}>
          {shown.map(gauge => (
            <GaugeMeter
              isCardFullScreen={isFullScreen}
              key={gauge.key}
              gauge={gauge}
              onHide={() => setShownKeys(toggleGaugeKey(shownKeys, gaugeKeys, gauge.key))}
            />
          ))}
        </div>
      )}

      {!showSkeleton && !isEmpty && shown.length === 0 && (
        <p className="flex-1 flex items-center justify-center text-sm text-theme-text-muted">
          Pick a metric above to show it.
        </p>
      )}
    </Paper>
  );
};
